import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  Play,
  FileText,
  Navigation,
  Thermometer,
  ChevronRight,
  Calendar,
  User,
  Clock,
  MoreHorizontal,
  Eye } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/shared/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
"@/shared/components/ui/tabs";
import { TrailStatsCard } from '../../components/trails/TrailStatsCard';
import { TrailEventTimeline } from '../../components/trails/TrailEventTimeline';
import { ShipmentList } from '../../components/trails/ShipmentCard';
import { cn } from '@/shared/utils';
// Mock data for trails
const mockTrailsStats = {
  totalShipments: 1247,
  activeRoutes: 23,
  temperatureAlerts: 5,
  auditEvents: 3456
};

const mockAuditTrail = [
{ id: 1, action: 'status_change', entityType: 'shipment', entityId: 'SHP-001', user: 'John Doe', timestamp: new Date().toISOString(), details: 'Status changed to In Transit' },
{ id: 2, action: 'location_update', entityType: 'shipment', entityId: 'SHP-002', user: 'System', timestamp: new Date().toISOString(), details: 'GPS location updated' }];


const mockGPSTrail = [];
const mockTemperatureTrail = [];
const mockShipmentsForReplay = [
{ id: 'SHP-001', trackingNumber: 'TRK-001', status: 'in_transit', origin: 'São Paulo', destination: 'Rio de Janeiro' }];


const actionTypes = [
{ value: 'status_change', label: 'Status Change', color: 'blue' },
{ value: 'location_update', label: 'Location Update', color: 'green' },
{ value: 'temperature_alert', label: 'Temperature Alert', color: 'red' }];


const entityTypes = [
{ value: 'shipment', label: 'Shipment', icon: 'Package' },
{ value: 'route', label: 'Route', icon: 'Navigation' }];


// Audit trail table row
const AuditTrailRow = ({ entry }) => {const { t } = useTranslation("ezparcel");
  const actionConfig = actionTypes.find((a) => a.value === entry.action) || { label: entry.action, color: 'gray' };
  const entityConfig = entityTypes.find((e) => e.value === entry.entityType) || { label: entry.entityType, icon: 'FileText' };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`audit-row-${entry.id}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
            <User className="h-4 w-4" />
          </div>
          <div>
            <div className="font-medium text-sm text-gray-900">{entry.user}</div>
            <div className="text-xs text-gray-500">{entry.device}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            actionConfig.color === 'emerald' && "bg-emerald-50 text-emerald-700 border-emerald-200",
            actionConfig.color === 'blue' && "bg-blue-50 text-blue-700 border-blue-200",
            actionConfig.color === 'red' && "bg-red-50 text-red-700 border-red-200",
            actionConfig.color === 'violet' && "bg-violet-50 text-violet-700 border-violet-200",
            actionConfig.color === 'cyan' && "bg-cyan-50 text-cyan-700 border-cyan-200",
            actionConfig.color === 'amber' && "bg-amber-50 text-amber-700 border-amber-200",
            actionConfig.color === 'orange' && "bg-orange-50 text-orange-700 border-orange-200",
            actionConfig.color === 'green' && "bg-green-50 text-green-700 border-green-200",
            actionConfig.color === 'gray' && "bg-gray-50 text-gray-700 border-gray-200"
          )}>
          
          {actionConfig.label}
        </Badge>
      </td>
      <td className="py-3 px-4">
        <div className="text-sm text-gray-900">{entry.entityType}</div>
        <div className="text-xs text-gray-500 font-mono">{entry.entityId}</div>
      </td>
      <td className="py-3 px-4">
        <div className="text-sm text-gray-700 max-w-xs truncate">{entry.description}</div>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          {new Date(entry.timestamp).toLocaleString()}
        </div>
      </td>
      <td className="py-3 px-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}

            </DropdownMenuItem>
            <DropdownMenuItem>
              <Download className="h-4 w-4 mr-2" />{t("export", "Export")}

            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>);

};

// Temperature trail card
const TemperatureTrailCard = ({ trail }) => {const { t } = useTranslation("ezparcel");
  const complianceColor = trail.compliance >= 95 ? 'emerald' : trail.compliance >= 80 ? 'amber' : 'red';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`temp-trail-${trail.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-medium text-gray-900">{trail.shipmentId}</div>
          <div className="text-xs text-gray-500 mt-0.5">{t("sensor", "Sensor:")}{trail.sensorId} · {trail.date}</div>
        </div>
        <Badge
          variant="outline"
          className={cn(
            complianceColor === 'emerald' && "bg-emerald-50 text-emerald-700 border-emerald-200",
            complianceColor === 'amber' && "bg-amber-50 text-amber-700 border-amber-200",
            complianceColor === 'red' && "bg-red-50 text-red-700 border-red-200"
          )}>
          
          {trail.compliance}{t("compliant", "% Compliant")}
        </Badge>
      </div>
      
      {/* Mini temperature chart placeholder */}
      <div className="h-16 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg mb-3 flex items-end px-2 pb-1 gap-0.5">
        {trail.readings.map((reading, idx) =>
        <div
          key={idx}
          className={cn(
            "flex-1 rounded-t transition-all",
            reading.temp > 8 ? "bg-red-400" : reading.temp > 6 ? "bg-amber-400" : "bg-emerald-400"
          )}
          style={{ height: `${Math.min(100, reading.temp / 12 * 100)}%` }} />

        )}
      </div>
      
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <div className="text-xs text-gray-500">{t("min", "Min")}</div>
          <div className="text-sm font-semibold text-blue-600">{trail.minTemp}{t("c", "\xB0C")}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("max", "Max")}</div>
          <div className={cn(
            "text-sm font-semibold",
            trail.maxTemp > 8 ? "text-red-600" : "text-emerald-600"
          )}>{trail.maxTemp}{t("c", "\xB0C")}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("avg", "Avg")}</div>
          <div className="text-sm font-semibold text-gray-900">{trail.avgTemp}{t("c", "\xB0C")}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("excursions", "Excursions")}</div>
          <div className={cn(
            "text-sm font-semibold",
            trail.excursions.length > 0 ? "text-red-600" : "text-emerald-600"
          )}>{trail.excursions.length}</div>
        </div>
      </div>
    </div>);

};

export const TrailsDashboard = () => {const { t } = useTranslation("ezparcel");
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  // Filter audit trail
  const filteredAuditTrail = mockAuditTrail.filter((entry) => {
    const matchesSearch = (entry.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.user || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (entry.entityId || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = actionFilter === 'all' || entry.action === actionFilter;
    const matchesEntity = entityFilter === 'all' || entry.entityType === entityFilter;
    return matchesSearch && matchesAction && matchesEntity;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="trails-dashboard">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("trailsSystem", "Trails System")}</h1>
            <p className="text-sm text-gray-500 mt-1">{t("completeAuditGps", "Complete audit, GPS, and temperature trail tracking with route replay")}

            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/ezparcel/trails/replay">
              <Button className="gap-2" data-testid="route-replay-btn">
                <Play className="h-4 w-4" />{t("routeReplay", "Route Replay")}

              </Button>
            </Link>
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />{t("exportAll", "Export All")}

            </Button>
          </div>
        </div>
      </div>
      
      {/* Stats cards */}
      <div className="mb-6">
        <TrailStatsCard stats={mockTrailsStats} />
      </div>
      
      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-gray-100">
            <FileText className="h-4 w-4" />{t("overview", "Overview")}

          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2 data-[state=active]:bg-gray-100">
            <FileText className="h-4 w-4" />{t("auditTrail", "Audit Trail")}

          </TabsTrigger>
          <TabsTrigger value="gps" className="gap-2 data-[state=active]:bg-gray-100">
            <Navigation className="h-4 w-4" />{t("gpsTrail", "GPS Trail")}

          </TabsTrigger>
          <TabsTrigger value="temperature" className="gap-2 data-[state=active]:bg-gray-100">
            <Thermometer className="h-4 w-4" />{t("temperatureTrail", "Temperature Trail")}

          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Audit Events */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">{t("recentAuditEvents", "Recent Audit Events")}</h3>
                <Button variant="ghost" size="sm" className="gap-1 text-gray-500" onClick={() => setSelectedTab('audit')}>{t("viewAll", "View All")}

                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4">
                <TrailEventTimeline
                  events={mockAuditTrail.slice(0, 5).map((e) => ({
                    ...e,
                    event: e.action
                  }))}
                  maxHeight={300} />
                
              </div>
            </div>
            
            {/* Shipments for Replay */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">{t("availableForReplay", "Available for Replay")}</h3>
                <Link to="/ezparcel/trails/replay">
                  <Button variant="ghost" size="sm" className="gap-1 text-gray-500">{t("viewAll", "View All")}

                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="p-4">
                <ShipmentList
                  shipments={mockShipmentsForReplay.slice(0, 3)}
                  compact />
                
              </div>
            </div>
          </div>
          
          {/* Temperature Trails */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{t("temperatureTrails", "Temperature Trails")}</h3>
              <Button variant="ghost" size="sm" className="gap-1 text-gray-500" onClick={() => setSelectedTab('temperature')}>{t("viewAll", "View All")}

                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mockTemperatureTrail.map((trail) =>
              <TemperatureTrailCard key={trail.id} trail={trail} />
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* Audit Trail Tab */}
        <TabsContent value="audit">
          <div className="bg-white rounded-xl border border-gray-200">
            {/* Filters */}
            <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search audit trail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="audit-search" />
                
              </div>
              
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[150px]" data-testid="action-filter">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allActions", "All Actions")}</SelectItem>
                  {actionTypes.map((action) =>
                  <SelectItem key={action.value} value={action.value}>
                      {action.label}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="entity-filter">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allEntities", "All Entities")}</SelectItem>
                  {entityTypes.map((entity) =>
                  <SelectItem key={entity.value} value={entity.value}>
                      {entity.label}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />{t("dateRange", "Date Range")}

              </Button>
              
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />{t("export", "Export")}

              </Button>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">{t("user", "User")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">{t("action", "Action")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">{t("entity", "Entity")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">{t("description", "Description")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4">{t("timestamp", "Timestamp")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAuditTrail.map((entry) =>
                  <AuditTrailRow key={entry.id} entry={entry} />
                  )}
                </tbody>
              </table>
            </div>
            
            {filteredAuditTrail.length === 0 &&
            <div className="text-center py-12 text-gray-500">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">{t("noAuditEntries", "No audit entries found")}</p>
                <p className="text-sm mt-1">{t("tryAdjustingYour", "Try adjusting your filters")}</p>
              </div>
            }
          </div>
        </TabsContent>
        
        {/* GPS Trail Tab */}
        <TabsContent value="gps">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">{t("gpsTrailMap", "GPS Trail Map")}</h3>
                <div className="h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Navigation className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">{t("selectAShipment", "Select a shipment to view GPS trail")}</p>
                    <Link to="/ezparcel/trails/replay">
                      <Button className="mt-4 gap-2">
                        <Play className="h-4 w-4" />{t("openRouteReplay", "Open Route Replay")}

                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">{t("shipmentsWithGps", "Shipments with GPS")}</h3>
                </div>
                <div className="p-4">
                  <ShipmentList
                    shipments={mockShipmentsForReplay.filter((s) => s.hasGPS)}
                    compact />
                  
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Temperature Trail Tab */}
        <TabsContent value="temperature">
          <div className="space-y-6">
            {/* Temperature trail cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mockTemperatureTrail.map((trail) =>
              <TemperatureTrailCard key={trail.id} trail={trail} />
              )}
            </div>
            
            {/* Shipments with temperature data */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">{t("shipmentsWithTemperature", "Shipments with Temperature Monitoring")}</h3>
              </div>
              <div className="p-4">
                <ShipmentList
                  shipments={mockShipmentsForReplay.filter((s) => s.hasTemp)} />
                
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};